// Single source of truth for every themeable color in the Portal.
//
// These constants feed BOTH color systems the app uses:
//   1. Fluent UI component chrome  -> src/theme/themes.ts (createTheme)
//   2. Hand-written CSS / shell    -> src/styles/theme.css (CSS variables)
//
// Keep the two in sync by only ever editing colors here and mirroring the
// semantic values into theme.css. Never hardcode a hex in a component again.

export type ThemeName = "light" | "dark";

// Fluent v8 palette ramps. Light is the stock Office palette; dark is a
// neutral-inverted ramp with a DESATURATED primary (#479ef5) so the accent
// does not vibrate against dark surfaces. Both pass WCAG AA for body text.
export const fluentPalettes = {
  light: {
    themePrimary: "#0078d4",
    themeLighterAlt: "#eff6fc",
    themeLighter: "#deecf9",
    themeLight: "#c7e0f4",
    themeTertiary: "#71afe5",
    themeSecondary: "#2b88d8",
    themeDarkAlt: "#106ebe",
    themeDark: "#005a9e",
    themeDarker: "#004578",
    neutralLighterAlt: "#faf9f8",
    neutralLighter: "#f3f2f1",
    neutralLight: "#edebe9",
    neutralQuaternaryAlt: "#e1dfdd",
    neutralQuaternary: "#d0d0d0",
    neutralTertiaryAlt: "#c8c6c4",
    neutralTertiary: "#a19f9d",
    neutralSecondary: "#605e5c",
    neutralPrimaryAlt: "#3b3a39",
    neutralPrimary: "#323130",
    neutralDark: "#201f1e",
    black: "#000000",
    white: "#ffffff",
    // Explicit so the pair below is visibly a ramp decision, not a default.
    whiteTranslucent40: "rgba(255, 255, 255, 0.4)",
  },
  dark: {
    themePrimary: "#479ef5",
    themeLighterAlt: "#03060a",
    themeLighter: "#0c1a29",
    themeLight: "#16304d",
    themeTertiary: "#2b5f99",
    themeSecondary: "#3f8bde",
    themeDarkAlt: "#59a7f6",
    themeDark: "#72b5f8",
    themeDarker: "#96c9fa",
    neutralLighterAlt: "#282828",
    neutralLighter: "#313131",
    neutralLight: "#3f3f3f",
    neutralQuaternaryAlt: "#484848",
    neutralQuaternary: "#4f4f4f",
    neutralTertiaryAlt: "#6d6d6d",
    neutralTertiary: "#c8c8c8",
    neutralSecondary: "#d0d0d0",
    neutralPrimaryAlt: "#dadada",
    neutralPrimary: "#f3f2f1",
    neutralDark: "#f4f4f4",
    black: "#f8f8f8",
    white: "#1f1f1f",
    // Translucent companion of the inverted `white` above. createTheme does NOT
    // invert this slot, so without it the dark theme inherits the light default
    // rgba(255,255,255,.4) — a white-wash Overlay behind every Panel/Modal and a
    // white shimmer fade in ShimmeredDetailsList.
    whiteTranslucent40: "rgba(31, 31, 31, 0.4)",
  },
} as const;

// --db-fg-subtle (src/styles/theme.css) is a CSS-only token, not derived from
// fluentPalettes.neutralTertiary above. Canonical values: light #a19f9d,
// dark #8a8886 (a step darker than neutralTertiary's #c8c8c8, chosen for
// subtler de-emphasis on dark surfaces). Keep theme.css as the source of
// truth for this token; note it here so the two files don't drift.

// Default Docubird brand blue for header bars: the Portal top bar, and the
// tenant app-color default (color picker + logo preview). Intentionally NOT
// themed — a brand color bar is expected to stay constant across light/dark.
// Mirrored in Docubird.WebAddInClient/src/theme/palette.ts.
export const BRAND_HEADER = "#006DEB";

// The color reported to the browser/OS chrome (mobile address bar) per theme.
export const metaThemeColor: Record<ThemeName, string> = {
  light: "#ffffff",
  dark: "#1f1f1f",
};
